import type { SupplierProductGetResponseChildType } from "@sellar-io/api-v2/types";
import { useRef, useState } from "react";
import { Alert } from "../../../../../components/alert/Alert";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import {
  ProductItemAvailabilitySection,
  type ProductItemAvailabilitySectionHandle,
} from "../../shared/ProductItemAvailabilitySection";
import {
  ProductItemPricingSection,
  type ProductItemPricingSectionHandle,
} from "../../shared/ProductItemPricingSection";
import { Button, H5, H6, Icon } from "@sellar-io/components";
import { useProductCatalogueMode } from "../../../../../hooks/useProductCatalogueMode";

export const ProductItemsPage = ({
  products,
  onComplete,
}: {
  products: SupplierProductGetResponseChildType[];
  onComplete: () => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const productCatalogueMode = useProductCatalogueMode();

  const pricingRef = useRef<ProductItemPricingSectionHandle>(null);
  const availabilityRef = useRef<ProductItemAvailabilitySectionHandle>(null);

  const currentProduct = products[currentIndex];
  const total = products.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === total - 1;

  if (!currentProduct) return null;

  const handleSaveAndAdvance = async (advance: () => void) => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await Promise.all([
        pricingRef.current?.save(),
        availabilityRef.current?.save(),
      ]);
      advance();
    } catch {
      setSaveError("Failed to save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = () => {
    handleSaveAndAdvance(() => setCurrentIndex((i) => i + 1));
  };

  const handleConfirm = () => {
    handleSaveAndAdvance(onComplete);
  };

  const handleBack = () => {
    setSaveError(null);
    if (!isFirst) {
      setCurrentIndex((i) => i - 1);
    }
  };

  return (
    <>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onComplete}
        title="Update product status"
        description="Review this product's pricing and product catalogue settings before making it active"
      />

      <DialogContent className="flex flex-col gap-6">
        <div className="flex flex-col items-center rounded-lg bg-zinc-100 p-2 text-center">
          <span className="text-zinc-500">
            Product item {currentIndex + 1} of {total}
          </span>
          {currentProduct.name && (
            <span className="text-lg font-semibold">
              {currentProduct.name.split(" • ")[1]}
            </span>
          )}
        </div>

        {saveError && <Alert type="error" message={saveError} />}

        <div className="flex flex-col gap-2">
          <div>
            <H5>Review pricing</H5>
            <H6>
              Your product cannot be listed if it doesn’t have a price set
              against it
            </H6>
          </div>
          <ProductItemPricingSection
            key={currentProduct.id}
            ref={pricingRef}
            productId={currentProduct.id}
            containerType={currentProduct.containerType}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div>
            <H5>
              {productCatalogueMode.isBasicMode
                ? "Review product visibility"
                : "Review product catalogue settings"}
            </H5>
            <H6>
              {productCatalogueMode.isBasicMode
                ? "Choose whether this product should be visible to customers."
                : "Your product will not be visible to customers unless it’s toggled on in the product catalogue they’re assigned"}
            </H6>
          </div>
          <ProductItemAvailabilitySection
            key={`availability-${currentProduct.id}`}
            ref={availabilityRef}
            product={currentProduct}
            mode={productCatalogueMode.isBasicMode ? "basic" : "advanced"}
          />
        </div>
      </DialogContent>

      <DialogCTA>
        {!isFirst && (
          <Button
            className="mr-auto"
            color="tertiary"
            onClick={handleBack}
            disabled={isSaving}
          >
            <Icon icon="CHEVRON_LEFT" /> Back
          </Button>
        )}
        {isLast ? (
          <Button loading={isSaving} onClick={handleConfirm}>
            Finish
          </Button>
        ) : (
          <Button loading={isSaving} onClick={handleNext}>
            Next
          </Button>
        )}
      </DialogCTA>
    </>
  );
};
